import React, { useState, useCallback, useEffect } from "react";
import { BarChart3, Sparkles, RefreshCw, Download, ChevronDown, FileText, FileSpreadsheet, Printer, Info, X, BarChart2 } from "lucide-react";
import AutoDeck from "../insights/AutoDeck";
import ImportWizard from "../insights/ImportWizard";
import ProjectBar from "../components/ProjectBar";
import { useProjects } from "../ProjectContext";
import { useFeature } from "../featureFlags";
import { getFreshToken } from "../api";
import { useBoardActivity } from "../insights/BoardActivityContext";
import { useBlueprintImport } from "../insights/BlueprintImportContext";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");

// AI Insights → Dashboard (roadmap #77). A full-working-area home for the auto-generated board +
// the user's pinned charts, with the chrome from the approved mock
// (design-drafts/ai-insights-dashboard-view-draft.html). Reuses the existing AutoDeck board
// (rendered chromeless) and the /ai/board* endpoints — no backend change.
//
// SCOPE (agreed): the FUNCTIONAL controls are the project selector, the All/Pinned/Auto filter,
// Refresh, and Export to PowerPoint. The advanced controls the mock shows — multiple NAMED
// dashboards, a dashboard-level DATE RANGE, and SCHEDULED DIGEST — need backend that doesn't
// exist yet (Phase 3), so here they render per the mock but are clearly marked "Preview" and
// only toast (Dev preview). Strip/gate the previews before Prod.
const CAP = 18;                 // shared graph cap (auto + pinned), informational here
const SEG = ["all", "pinned", "auto"];
const SEG_LABEL = { all: "All", pinned: "Pinned", auto: "Auto" };
// Named dashboards + dashboard-level date range are not built yet (roadmap #77 #3, HELD). Their
// non-functional preview controls were removed from the dashboard so it shows only working
// controls; re-add when the backend lands.

// ── Template control (fixed-visual-blueprint R21.1/R21.3, relocated + leaned 2026-09-04) ──────
// Icon-only before any template exists (near-zero visual cost for the majority of customers who
// never use this) — outcome-framed tooltip, not the internal mechanism name. Becomes a named
// dropdown once a Blueprint is active. Never both states; hidden entirely when `available` is
// false (flag OFF — byte-for-byte unchanged for a flag-OFF tenant, same guarantee the old
// Analyze banner had).
// ── Concepts settings (blueprint-tables-and-concepts, Task 23) — list/retire confirmed Concepts
// for this project. No editable-in-place UI (retire + re-derive via the wizard's clarification
// flow is the only path today, mirroring how a chart's mapping has no re-pick UI either, per
// ImportWizard.jsx's own documented scope note) — a "delete and redo via the Tables step" model,
// consistent with the rest of this feature's v1 scope. ──
function ConceptsSettingsModal({ call, onClose }) {
  const [concepts, setConcepts] = useState(null);   // null = loading
  const [err, setErr] = useState("");
  const [retiringKey, setRetiringKey] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const d = await call("/blueprint/concepts", "GET");
      setConcepts(d.concepts || []);
    } catch (e) {
      setErr("Couldn't load your Concepts. Please try again.");
      setConcepts([]);
    }
  }, [call]);

  useEffect(() => { load(); }, [load]);

  const retire = async (concept) => {
    if (!window.confirm(`Retire "${concept.label || concept.key}"? Any cell relying on it will need a re-upload or a new explanation.`)) return;
    setRetiringKey(concept.key);
    try {
      await call(`/blueprint/concepts/${encodeURIComponent(concept.key)}?scope=${concept.scope || "project"}`, "DELETE");
      setConcepts((prev) => prev.filter((c) => c.key !== concept.key));
    } catch (e) {
      setErr("Couldn't retire this Concept. Please try again.");
    } finally {
      setRetiringKey(null);
    }
  };

  return (
    <div style={modalOverlayDp} onClick={onClose}>
      <div style={modalCardDp} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Manage Concepts</div>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>
        <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 14px", lineHeight: 1.5 }}>
          Concepts are words from your reports Boomerang couldn't match to a column by name alone — you explained them once, and every future occurrence resolves automatically.
        </p>
        {err && (
          <div style={{ marginBottom: 12, fontSize: 12.5, color: "#991b1b", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px" }}>{err}</div>
        )}
        {concepts === null ? (
          <div style={{ fontSize: 12.5, color: "#94a3b8", textAlign: "center", padding: "20px 0" }}>Loading…</div>
        ) : concepts.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "#94a3b8", textAlign: "center", padding: "20px 0" }}>
            No Concepts defined yet — they're created from the Tables step of "Match my exact report" when a cell needs a quick explanation.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {concepts.map((c) => (
              <div key={c.key} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{c.label || c.key}</div>
                    {c.explanation && <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>{c.explanation}</div>}
                    <div style={{ fontSize: 11, fontFamily: "monospace", color: "#475569", marginTop: 4 }}>{c.dsl}</div>
                    <div style={{ display: "flex", gap: 8, marginTop: 6, fontSize: 10.5, color: "#94a3b8" }}>
                      <span style={{ textTransform: "uppercase", letterSpacing: ".03em", fontWeight: 700, background: c.scope === "tenant" ? "#eef2ff" : "#f1f5f9", color: c.scope === "tenant" ? "#4338ca" : "#64748b", borderRadius: 999, padding: "1px 7px" }}>
                        {c.scope === "tenant" ? "All projects" : "This project"}
                      </span>
                      {c.confirmedAt && <span>Confirmed {new Date(c.confirmedAt).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <button onClick={() => retire(c)} disabled={retiringKey === c.key}
                    style={{ fontSize: 11.5, fontWeight: 600, padding: "5px 10px", borderRadius: 999, border: "1px solid #fecaca", background: "#fff", color: "#991b1b", cursor: "pointer", whiteSpace: "nowrap", opacity: retiringKey === c.key ? 0.6 : 1 }}>
                    {retiringKey === c.key ? "Retiring…" : "Retire"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const modalOverlayDp = { position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
const modalCardDp = { width: "min(560px,94vw)", maxHeight: "82vh", overflow: "auto", background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,.3)" };

function TemplateControl({ modeInfo, busy, onUpload, onEdit, onSwitchMode, onManageConcepts }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (!modeInfo || !modeInfo.available) return null;

  const btnBase = { border: "1px solid #e2e8f0", background: "#fff", borderRadius: "10px", padding: "8px 11px", fontSize: "13px", fontWeight: 700, color: "#334155", cursor: busy ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" };

  if (modeInfo.mode === "blueprint" && modeInfo.blueprintPublished) {
    return (
      <div style={{ position: "relative" }}>
        <button style={{ ...btnBase, background: "#eef2ff", borderColor: "#c7d2fe", color: "#3730a3" }} disabled={busy}
          onClick={() => setMenuOpen((o) => !o)} aria-haspopup="menu" aria-expanded={menuOpen}
          title="Your board is rendering your uploaded template's exact charts">
          <BarChart2 size={13} /> Your Template <ChevronDown size={12} />
        </button>
        {menuOpen && (
          <>
            <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
            <div role="menu" style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50, background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", boxShadow: "0 10px 28px rgba(15,23,42,.15)", minWidth: "200px", padding: "6px" }}>
              <button role="menuitem" onClick={() => { setMenuOpen(false); onEdit(); }}
                style={{ display: "flex", alignItems: "center", gap: "9px", width: "100%", textAlign: "left", padding: "9px 10px", border: "none", background: "transparent", color: "#0f172a", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                Edit Template
              </button>
              {/* Concepts are project-scoped independent of publish status (blueprint-tables-
                  and-concepts R5.5) — surfaced here too, not gated to "already published", so a
                  Concept confirmed mid-wizard before a first publish is still manageable. */}
              <button role="menuitem" onClick={() => { setMenuOpen(false); onManageConcepts(); }}
                style={{ display: "flex", alignItems: "center", gap: "9px", width: "100%", textAlign: "left", padding: "9px 10px", border: "none", background: "transparent", color: "#0f172a", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                Manage Concepts
              </button>
              <button role="menuitem" onClick={() => { setMenuOpen(false); setConfirmOpen(true); }}
                style={{ display: "flex", alignItems: "center", gap: "9px", width: "100%", textAlign: "left", padding: "9px 10px", border: "none", background: "transparent", color: "#64748b", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                🔄 Switch back to Smart AI
              </button>
            </div>
          </>
        )}
        {confirmOpen && (
          <div onClick={() => setConfirmOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: "min(460px,92vw)", background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Switch back to Smart AI?</div>
              <p style={{ fontSize: 12.5, color: "#64748b", margin: "10px 0 18px", lineHeight: 1.5 }}>
                Your template charts will be hidden and replaced with AI-generated insights.
                Your pinned/added charts stay exactly as they are.
                Your template isn't deleted — you can switch back to it anytime.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button onClick={() => setConfirmOpen(false)} style={{ fontSize: 13, fontWeight: 600, padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#334155", cursor: "pointer" }}>Cancel</button>
                <button onClick={() => { setConfirmOpen(false); onSwitchMode("ai"); }} style={{ fontSize: 13, fontWeight: 700, padding: "8px 16px", borderRadius: 8, border: "none", background: "#4f46e5", color: "#fff", cursor: "pointer" }}>Switch to Smart AI</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // No Blueprint published yet — icon-only button (leaner than a full-width banner; a returning
  // customer who never uses this feature carries near-zero visual weight). A small dropdown
  // (rather than a single click straight to upload) so "Manage Concepts" is reachable even
  // before a first publish — a customer can define a Concept mid-wizard, abandon before
  // publishing, and still need a way back to it (Concepts are project-scoped, independent of
  // publish status, R5.5).
  return (
    <div style={{ position: "relative" }}>
      <button style={btnBase} disabled={busy} onClick={() => setMenuOpen((o) => !o)}
        aria-haspopup="menu" aria-expanded={menuOpen}
        title="Match my exact report — upload the Excel/PowerPoint you already build" aria-label="Match my exact report">
        <BarChart2 size={13} />
      </button>
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div role="menu" style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50, background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", boxShadow: "0 10px 28px rgba(15,23,42,.15)", minWidth: "200px", padding: "6px" }}>
            <button role="menuitem" onClick={() => { setMenuOpen(false); onUpload(); }}
              style={{ display: "flex", alignItems: "center", gap: "9px", width: "100%", textAlign: "left", padding: "9px 10px", border: "none", background: "transparent", color: "#0f172a", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              Match my exact report
            </button>
            <button role="menuitem" onClick={() => { setMenuOpen(false); onManageConcepts(); }}
              style={{ display: "flex", alignItems: "center", gap: "9px", width: "100%", textAlign: "left", padding: "9px 10px", border: "none", background: "transparent", color: "#0f172a", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              Manage Concepts
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Corner activity pill (fixed-visual-blueprint R21.8) — replaces an earlier in-page status
// card: visible from ANY page (not gated to Dashboard being active), never resizes page content,
// collapses to nothing once dismissed/consumed. Mirrors the background-upload-toast pattern
// (Drive/Slack) customers already recognize.
function ImportActivityPill({ importState, onReview, onDismissError }) {
  if (!importState) return null;
  if (importState.status === "uploading") {
    return (
      <div style={{ position: "fixed", bottom: "22px", right: "22px", zIndex: 60, display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #c7d2fe", borderRadius: "999px", padding: "10px 16px", boxShadow: "0 12px 32px rgba(15,23,42,.18)" }}>
        <RefreshCw size={14} className="animate-spin" style={{ color: "#4f46e5" }} />
        <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#3730a3" }}>Processing your template…</span>
      </div>
    );
  }
  if (importState.status === "error") {
    return (
      <div style={{ position: "fixed", bottom: "22px", right: "22px", zIndex: 60, display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #fecaca", borderRadius: "999px", padding: "10px 16px", boxShadow: "0 12px 32px rgba(15,23,42,.18)" }}>
        <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#991b1b" }}>⚠ Template import failed</span>
        <button onClick={onReview} style={{ fontSize: "12px", fontWeight: 700, padding: "4px 10px", borderRadius: "999px", border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", cursor: "pointer" }}>Try again</button>
        <button onClick={onDismissError} style={{ border: "none", background: "transparent", color: "#94a3b8", cursor: "pointer", padding: "2px" }}><X size={13} /></button>
      </div>
    );
  }
  if (importState.status === "ready") {
    return (
      <div style={{ position: "fixed", bottom: "22px", right: "22px", zIndex: 60, display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #a7f3d0", borderRadius: "999px", padding: "10px 16px", boxShadow: "0 12px 32px rgba(15,23,42,.18)" }}>
        <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#065f46" }}>✅ Your template is ready</span>
        <button onClick={onReview} style={{ fontSize: "12px", fontWeight: 700, padding: "4px 12px", borderRadius: "999px", border: "none", background: "#059669", color: "#fff", cursor: "pointer" }}>Review &amp; publish</button>
      </div>
    );
  }
  return null;
}

export default function DashboardPage({ idToken, onGoAnalyze }) {
  const { activeProjectId } = useProjects();
  const brandColorsOn = useFeature("auto_insights_brand_colors");
  const [filter, setFilter] = useState("all");            // all | pinned | auto (functional)
  const [regenSignal, setRegenSignal] = useState(0);      // bump → AutoDeck force-regenerates (same as "Regenerate board")
  const [meta, setMeta] = useState({ status: "", total: 0, pinned: 0, auto: 0, tables: 0 });
  const [toastMsg, setToastMsg] = useState(null);
  const [busy, setBusy] = useState(false);                // export in flight
  // Board "working" comes from the App-level activity context (survives section switches), not
  // local state — so the Refresh spinner stays in sync with the Analyze "Regenerate board" one.
  const { working: boardActivity, beginWork } = useBoardActivity();
  const working = !!(activeProjectId && boardActivity[activeProjectId]);
  const [exportOpen, setExportOpen] = useState(false);   // multi-format export menu
  const [noteDismissed, setNoteDismissed] = useState(() => {
    try { return localStorage.getItem("dash_gen_note_dismissed") === "1"; } catch { return false; }
  });
  const dismissNote = () => { setNoteDismissed(true); try { localStorage.setItem("dash_gen_note_dismissed", "1"); } catch {} };

  const toast = (m) => { setToastMsg(m); window.clearTimeout(toast._t); toast._t = window.setTimeout(() => setToastMsg(null), 2800); };
  const onBoardMeta = useCallback((m) => setMeta(m || { status: "", total: 0, pinned: 0, auto: 0, tables: 0 }), []);

  // ── Fixed Visual Blueprint entry point (fixed-visual-blueprint R21, relocated 2026-09-04) ──
  // Was a banner/indicator inside Analyze (AutoDeck.jsx's TemplateBanner) — moved here because
  // Analyze loads scrolled to the bottom of chat history, so anything rendered above the board
  // was permanently below the fold on a project with real conversation history. Dashboard never
  // auto-scrolls and already owns a persistent control bar, so the control lives there instead.
  const call = useCallback(async (path, method = "GET", body) => {
    let token = await getFreshToken();
    const opts = { method, headers: { Authorization: `Bearer ${token}`, "X-Project-Id": activeProjectId } };
    if (body) { opts.headers["Content-Type"] = "application/json"; opts.body = JSON.stringify(body); }
    let res = await fetch(`${API_URL}${path}`, opts);
    if (res.status === 401) { token = await getFreshToken(); opts.headers.Authorization = `Bearer ${token}`; res = await fetch(`${API_URL}${path}`, opts); }
    if (!res.ok) {
      // Same message as before (the status code) so every existing catch behaves identically;
      // `status` + the parsed `body` let a caller show the server's own reason (#188: a pinned
      // table blocked at publish says why).
      const err = new Error(`${res.status}`);
      err.status = res.status;
      err.body = await res.json().catch(() => null);
      throw err;
    }
    return res.json();
  }, [activeProjectId]);

  const [modeInfo, setModeInfo] = useState(null);   // {mode, available, blueprintPublished}
  const [wizardOpen, setWizardOpen] = useState(false);
  const [conceptsOpen, setConceptsOpen] = useState(false);
  const { getImport: getBlueprintImport, clearImport: clearBlueprintImport } = useBlueprintImport();
  const blueprintImportState = getBlueprintImport(activeProjectId);

  useEffect(() => {
    let cancelled = false;
    if (!activeProjectId) return undefined;
    (async () => {
      try {
        const d = await call("/ai/board/mode");
        if (!cancelled && d && d.enabled) setModeInfo(d);
      } catch (e) { /* non-blocking: no control shown */ }
    })();
    return () => { cancelled = true; };
  }, [call, activeProjectId]);

  const switchBoardMode = async (mode) => {
    if (!modeInfo || modeInfo.mode === mode) return;
    setBusy(true);
    try {
      const d = await call("/ai/board/mode", "PUT", { mode });
      if (d && d.enabled) {
        setModeInfo((m) => ({ ...(m || {}), mode }));
        setRegenSignal((n) => n + 1);   // AutoDeck re-fetches/re-renders in the new mode
        // Non-blocking notice, e.g. blueprint mode on but nothing published yet (R1.4) — was a
        // dismissible inline banner on Analyze (AutoDeck.jsx's old modeNotice); a toast here
        // keeps that informational surface without reintroducing in-page banner weight.
        if (d.notice) toast(d.notice);
      }
    } catch (e) { toast("Couldn't switch the board mode."); }
    finally { setBusy(false); }
  };

  // Server exports (roadmap #77 multi-format): PowerPoint (native charts) + Excel (board data).
  const downloadBoard = async (kind) => {
    setExportOpen(false);
    setBusy(true);
    try {
      const token = await getFreshToken();
      const res = await fetch(`${API_URL}/ai/board/export.${kind}`, {
        headers: { Authorization: `Bearer ${token}`, "X-Project-Id": activeProjectId },
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `boomerang-dashboard-${activeProjectId}.${kind}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast("Couldn't build that export just now — is the board finished building?"); }
    finally { setBusy(false); }
  };

  // PDF = browser print of the board (uses the real rendered charts). A print stylesheet
  // (`body.board-print`, in MainLayout.css) hides the app chrome and expands the board so the
  // OS "Save as PDF" produces a clean board-only document. No new backend dependency.
  const printBoard = () => {
    setExportOpen(false);
    document.body.classList.add("board-print");
    const cleanup = () => { document.body.classList.remove("board-print"); window.removeEventListener("afterprint", cleanup); };
    window.addEventListener("afterprint", cleanup);
    setTimeout(() => window.print(), 80);   // let the menu close before the print dialog opens
  };

  // ── styles (light content area, consistent with the app) ──
  const btn = { border: "1px solid #e2e8f0", background: "#fff", borderRadius: "10px", padding: "8px 13px", fontSize: "13px", fontWeight: 700, color: "#334155", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "7px", whiteSpace: "nowrap" };
  const btnPrimary = { ...btn, background: "#6366f1", borderColor: "#6366f1", color: "#fff" };
  const segWrap = { display: "flex", gap: "3px", background: "#f1f4f9", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "3px" };
  const segBtn = (on) => ({ border: "none", background: on ? "#fff" : "transparent", color: on ? "#0f172a" : "#64748b", boxShadow: on ? "0 1px 2px rgba(0,0,0,.08)" : "none", padding: "6px 13px", borderRadius: "8px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer" });
  const menuItem = { display: "flex", alignItems: "center", gap: "9px", width: "100%", textAlign: "left", padding: "9px 10px", border: "none", background: "transparent", color: "#0f172a", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 };

  return (
    <div className="dashboard-fullbleed" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 60px)", minWidth: 0, background: "#f6f7fb" }}>
      {/* ── ONE cohesive control bar: identity + cap (left) · project / filter / date range / actions (right) ── */}
      <div className="dash-controlbar" style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", padding: "10px 20px", borderBottom: "1px solid #e2e8f0", background: "#fff" }}>
        <div style={{ width: "30px", height: "30px", borderRadius: "8px", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <BarChart3 size={16} color="#fff" />
        </div>
        <h1 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", margin: 0, letterSpacing: "-0.01em" }}>Dashboard</h1>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, color: "#4f46e5", background: "#eef2ff", border: "1px solid #e0e7ff" }}>
          <Sparkles size={11} /> Auto-Insights
        </span>
        <span style={{ fontSize: "12px", fontWeight: 800, color: meta.total >= CAP ? "#9f1239" : "#3730a3", background: meta.total >= CAP ? "#fff1f3" : "#eef2ff", border: `1px solid ${meta.total >= CAP ? "#fecdd3" : "#c7d2fe"}`, borderRadius: "999px", padding: "3px 10px", whiteSpace: "nowrap" }}>
          {meta.total} / {CAP} charts{meta.tables > 0 ? ` · ${meta.tables} table${meta.tables === 1 ? "" : "s"}` : ""}
        </span>

        <div style={{ flex: 1, minWidth: "8px" }} />

        <ProjectBar inline />
        {/* All / Pinned / Auto (functional) */}
        <div style={segWrap} role="tablist" aria-label="Card filter">
          {SEG.map((f) => (
            <button key={f} role="tab" aria-selected={filter === f} style={segBtn(filter === f)} onClick={() => setFilter(f)}>{SEG_LABEL[f]}</button>
          ))}
        </div>
        <button style={btn} onClick={() => onGoAnalyze && onGoAnalyze()} title="Create a chart by asking, then pin it here">
          <Sparkles size={13} /> Add from AI Insights →
        </button>
        <button style={{ ...btn, opacity: working ? 0.7 : 1 }} disabled={busy || working}
          onClick={() => { beginWork(activeProjectId); setRegenSignal((n) => n + 1); toast("Regenerating your board from the latest data…"); }}
          title="Rebuild every auto card from your latest data (same as Regenerate board in Analyze)">
          <RefreshCw size={13} className={working ? "animate-spin" : undefined} /> {working ? "Refreshing…" : "Refresh"}
        </button>
        {/* Scheduled digest removed (roadmap: deferred — not needed now). */}
        {/* Template entry point (fixed-visual-blueprint R21, relocated 2026-09-04) — icon-only
            until active, sits next to Export so it reads as a board-shaping control, not a
            standalone feature. */}
        <TemplateControl modeInfo={modeInfo} busy={busy}
          onUpload={() => setWizardOpen(true)}
          onEdit={() => setWizardOpen(true)}
          onSwitchMode={switchBoardMode}
          onManageConcepts={() => setConceptsOpen(true)} />
        {/* Multi-format export dropdown (roadmap #77): PowerPoint · Excel · PDF (print view). */}
        <div style={{ position: "relative" }} className="dash-export">
          <button style={btnPrimary} disabled={busy} onClick={() => setExportOpen((o) => !o)} aria-haspopup="menu" aria-expanded={exportOpen}>
            <Download size={13} /> Export <ChevronDown size={13} />
          </button>
          {exportOpen && (
            <>
              <div onClick={() => setExportOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div role="menu" style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50, background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", boxShadow: "0 10px 28px rgba(15,23,42,.15)", minWidth: "218px", padding: "6px" }}>
                <button role="menuitem" style={menuItem} onClick={() => downloadBoard("pptx")}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <FileText size={15} color="#b7472a" /> PowerPoint <span style={{ color: "#94a3b8", fontWeight: 500 }}>.pptx</span>
                </button>
                <button role="menuitem" style={menuItem} onClick={() => downloadBoard("xlsx")}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <FileSpreadsheet size={15} color="#217346" /> Excel data <span style={{ color: "#94a3b8", fontWeight: 500 }}>.xlsx</span>
                </button>
                <button role="menuitem" style={menuItem} onClick={printBoard}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <Printer size={15} color="#475569" /> PDF <span style={{ color: "#94a3b8", fontWeight: 500 }}>print view</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Working strip — a clear, always-visible indicator that a refresh/regenerate is running
          (AutoDeck's own "Working…" state is hidden in chromeless mode). Animated so it never
          looks stuck; shows while the POST is in flight AND while visuals stream in. */}
      {working && (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 20px", background: "#eef2ff", borderBottom: "1px solid #c7d2fe", color: "#4f46e5", fontSize: "13px", fontWeight: 700 }}>
          <RefreshCw size={15} className="animate-spin" />
          Refreshing your board from the latest data… charts update as they finish.
        </div>
      )}

      {/* ── Board (the existing AutoDeck, chromeless — this page owns the chrome) ── */}
      <div className="dash-board-scroll" style={{ flex: 1, overflowY: "auto", padding: "16px 20px 44px" }}>
        {/* How-this-is-generated note (dismissible, remembered) — sets expectations that the board
            reflects the CURRENT data and that chat answers build on recent questions. */}
        {!noteDismissed && (
          <div className="dash-navstrip" style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px 14px", marginBottom: "14px", background: "#eff4ff", border: "1px solid #d6e0ff", borderRadius: "12px" }}>
            <Info size={16} color="#4f46e5" style={{ flexShrink: 0, marginTop: "1px" }} />
            <div style={{ fontSize: "12.5px", color: "#334155", lineHeight: 1.5, flex: 1 }}>
              This dashboard is auto-generated from your <b>current uploaded data</b> and refreshes when your data changes. In <b>Analyze</b>, chat answers and charts also build on your recent questions — use <b>clear history</b> there to ask a fresh, independent question.
            </div>
            <button onClick={dismissNote} title="Dismiss" style={{ border: "none", background: "transparent", color: "#94a3b8", cursor: "pointer", padding: "2px", flexShrink: 0 }}>
              <X size={15} />
            </button>
          </div>
        )}
        <AutoDeck
          projectId={activeProjectId}
          chromeless
          filter={filter}
          regenerateSignal={regenSignal}
          onBoardMeta={onBoardMeta}
          brandColorsOn={brandColorsOn}
          onDrill={() => { if (onGoAnalyze) onGoAnalyze(); }}
        />
      </div>

      {/* toast */}
      {toastMsg && (
        <div style={{ position: "fixed", bottom: "22px", left: "50%", transform: "translateX(-50%)", background: "#0f172a", color: "#fff", padding: "12px 18px", borderRadius: "12px", fontSize: "13.5px", fontWeight: 600, boxShadow: "0 20px 50px rgba(0,0,0,.35)", zIndex: 50 }}>
          {toastMsg}
        </div>
      )}

      {/* Import wizard (fixed-visual-blueprint task 27) — launched ONLY from TemplateControl
          above (R21.1). Closing without publishing leaves boardMode/Blueprint untouched; the
          upload/extract request itself survives in BlueprintImportContext regardless. */}
      {wizardOpen && (
        <ImportWizard projectId={activeProjectId} call={call}
          onClose={() => setWizardOpen(false)}
          onPublished={async () => {
            setWizardOpen(false);
            try { const d = await call("/ai/board/mode"); if (d && d.enabled) setModeInfo(d); } catch (e) { /* keep prior */ }
            setRegenSignal((n) => n + 1);
          }} />
      )}

      {/* Concepts settings (blueprint-tables-and-concepts, Task 23) — reachable from
          TemplateControl in EITHER state (pre- or post-publish), since Concepts are
          project-scoped independent of publish status. */}
      {conceptsOpen && <ConceptsSettingsModal call={call} onClose={() => setConceptsOpen(false)} />}

      {/* Corner activity pill (R21.8) — visible regardless of wizard open/closed state, so
          closing the wizard mid-import doesn't lose the only visible sign it's still running. */}
      {!wizardOpen && (
        <ImportActivityPill importState={blueprintImportState}
          onReview={() => setWizardOpen(true)}
          onDismissError={() => clearBlueprintImport(activeProjectId)} />
      )}
    </div>
  );
}
